import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  HandoverDetailHeader,
  HandoverDetailRecord,
  HandoverDetailView,
  HandoverStatusNotification as StatusNotification,
  formatHandoverDateTime as formatDateTime,
  resolveHandoverPlate,
} from '@/components';
import { ReturnSourceType } from '@/types/common';
import { getHandoverById } from '@/services/api/handovers';
import { showErrorToast } from '@/components/toast/globalToast';
import { extractApiErrorMessage } from '@/utils';
import { useListReturnPath } from '@/hooks/useUrlListState';

/**
 * The detail shape now lives with the shared handover detail components, so
 * the rent screen reads the same record this page does.
 */
type HandoverDetail = HandoverDetailRecord;

const HandoverDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backTo = useListReturnPath('/handovers');
  const [handover, setHandover] = useState<HandoverDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;

    const fetchDetail = async () => {
      setIsLoading(true);
      try {
        const response = await getHandoverById(id);
        setHandover(response || null);
      } catch (error) {
        const message = extractApiErrorMessage(
          error,
          'Failed to load handover detail'
        );
        showErrorToast('Error', message);
        setHandover(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  const titlePlate = resolveHandoverPlate(handover);
  const subtitleDate = handover?.returnDate || handover?.updatedAt;
  const subtitleBy = handover?.updatedBy || handover?.createdBy;
  const headerSubtitle = subtitleDate
    ? `Returned Vehicle on ${formatDateTime(subtitleDate)}`
    : '';
  const headerSubtitleText = headerSubtitle
    ? `${headerSubtitle}${subtitleBy ? ` by ${subtitleBy}` : ''}`
    : '';

  const returnSourceId = handover?.returnSourceID || '';
  const returnSourceCode = handover?.returnSourceCode || '';
  const returnSource = handover?.returnSourceType;
  const isIncidentReturn = returnSource === ReturnSourceType.INCIDENT;
  const isRepoReturn = returnSource === ReturnSourceType.REPOSSESSION;
  const isMaintenanceReturn = returnSource === ReturnSourceType.MAINTENANCE;
  const returnSourceLabel = returnSourceCode || returnSourceId;
  const actionLabel = returnSourceLabel
    ? `View ${returnSourceLabel}`
    : isIncidentReturn
      ? 'View INCIDENT'
      : isMaintenanceReturn
        ? 'View MAINTENANCE'
        : 'View REPO';

  if (isLoading) {
    return (
      <div className="bg-white p-6 max-w-full mx-auto">
        <div className="text-sm text-gray-500">Loading handover detail...</div>
      </div>
    );
  }

  if (!handover) {
    return (
      <div className="bg-white p-6 max-w-full mx-auto">
        <div className="text-sm text-gray-500">Handover detail not found.</div>
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="mt-4 text-sm font-medium text-purple-600"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <HandoverDetailHeader
        title={`Returned Handover - ${titlePlate}`}
        subtitle={headerSubtitleText}
        onBack={() => navigate(backTo)}
        actionButton={
          isIncidentReturn || isRepoReturn || isMaintenanceReturn ? (
            <Button
              variant="primary"
              size="small"
              onClick={() => {
                if (isIncidentReturn) {
                  if (!returnSourceId) return;
                  navigate(`/incidents/${returnSourceId}`);
                  return;
                }
                if (isMaintenanceReturn) {
                  if (!returnSourceId) return;
                  navigate(`/maintenances/${returnSourceId}`);
                  return;
                }
                if (!returnSourceId) return;
                navigate(`/repossessions/${returnSourceId}`);
              }}
              disabled={!returnSourceId}
              className="rounded-full px-4"
            >
              {actionLabel}
            </Button>
          ) : undefined
        }
      />

      {(isIncidentReturn || isRepoReturn || isMaintenanceReturn) && (
        <div className="mb-6">
          <StatusNotification
            title={
              isIncidentReturn
                ? 'Vehicle Returned with Damage'
                : isMaintenanceReturn
                  ? 'Vehicle Returned via Maintenance'
                  : 'Vehicle Returned via Repossession'
            }
            message={
              isIncidentReturn
                ? 'An incident report was filed during this return handover. The vehicle requires maintenance.'
                : isMaintenanceReturn
                  ? 'This return handover was generated from a maintenance case with a permanent swap.'
                  : 'This return handover was generated from a repossession case.'
            }
          />
        </div>
      )}

      <HandoverDetailView handover={handover} />
    </div>
  );
};

export default HandoverDetailPage;
